import { sumBy } from 'es-toolkit';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  Award,
  AwardExecutiveSummaryFileTemplate,
  AwardForStaff,
  AwardGroup,
  AwardGroupForStaff,
  AwardLead,
  DropdownOption,
  EligibilityRule,
  GroupLead,
  JudgingQuestion
} from '../../models';
import {
  JudgingQuestionValidation,
  judgingQuestionValidationSchema,
} from '../../models/schemas/judging-question-validation.schema';
import { createValidationState, ValidationState } from '../../shared/validation';

export class EditAwardDetailPageUiStore {
  @observable isBeingValidated: boolean = false;

  @observable awardGroups: AwardGroup[] = [];

  @observable selectedAwardGroupForStaff?: AwardGroupForStaff;

  @observable editedAwardGroupForStaff?: AwardGroupForStaff;

  @observable updatedAwardGroupsForStaff: AwardGroupForStaff[] = [];

  @observable groupLeadsToDelete: GroupLead[] = [];

  @observable selectedAwardForStaff?: AwardForStaff;

  @observable editedAwardForStaff?: AwardForStaff;

  @observable updatedAwardsForStaff: AwardForStaff[] = [];

  @observable judgingQuestionsToDelete: JudgingQuestion[] = [];

  @observable awardLeadsToDelete: AwardLead[] = [];

  @observable awardTemplatesToDelete: AwardExecutiveSummaryFileTemplate[] = [];

  @observable selectedEligibilityRules: EligibilityRule[] = [];

  @observable specializationOptions: DropdownOption<string>[] = [];

  @observable designationOptions: DropdownOption<string>[] = [];

  @observable judgeCapacityOptions: DropdownOption<number>[] = [];

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.awardGroups = [];
    this.selectedAwardGroupForStaff = undefined;
    this.editedAwardGroupForStaff = undefined;
    this.updatedAwardGroupsForStaff = [];
    this.selectedAwardForStaff = undefined;
    this.editedAwardForStaff = undefined;
    this.updatedAwardsForStaff = [];
    this.judgingQuestionsToDelete = [];
    this.awardLeadsToDelete = [];
    this.groupLeadsToDelete = [];
    this.awardTemplatesToDelete = [];
    this.judgeCapacityOptions = [];
    this.specializationOptions = [];
    this.designationOptions = [];
    this.isBeingValidated = false;
  }

  resetAwardForStaffData(): void {
    this.selectedAwardGroupForStaff = undefined;
    this.editedAwardGroupForStaff = undefined;
    this.updatedAwardGroupsForStaff = [];
    this.selectedAwardForStaff = undefined;
    this.editedAwardForStaff = undefined;
    this.updatedAwardsForStaff = [];
    this.judgingQuestionsToDelete = [];
    this.awardLeadsToDelete = [];
    this.groupLeadsToDelete = [];
    this.awardTemplatesToDelete = [];
    this.isBeingValidated = false;
  }

  @action
  setIsBeingValidated(isBeingValidated: boolean): void {
    this.isBeingValidated = isBeingValidated;
  }

  @action
  setAwardGroups(awardGroups: AwardGroup[]): void {
    this.awardGroups = awardGroups;
  }

  @action
  setSelectedAwardGroupForStaff(selectedAwardGroupForStaff?: AwardGroupForStaff): void {
    this.selectedAwardGroupForStaff = selectedAwardGroupForStaff;
  }

  @action
  setEditedAwardGroupForStaff(selectedAwardGroupForStaff?: AwardGroupForStaff): void {
    this.editedAwardGroupForStaff = selectedAwardGroupForStaff;
  }

  @action
  setSelectedAwardForStaff(selectedAward?: AwardForStaff): void {
    this.selectedAwardForStaff = selectedAward;
  }

  @action
  setEditedAwardForStaff(selectedAward?: AwardForStaff): void {
    this.editedAwardForStaff = selectedAward;
  }

  @action
  addUpdatedAwardForStaff(awardForStaff: AwardForStaff): void {
    this.updatedAwardsForStaff.push(awardForStaff);
  }

  @action
  removeUpdatedAwardForStaffAt(index: number): void {
    this.updatedAwardsForStaff.splice(index, 1);
  }

  @action
  addUpdatedAwardGroupForStaff(awardGroupForStaff: AwardGroupForStaff): void {
    this.updatedAwardGroupsForStaff.push(awardGroupForStaff);
  }

  @action
  removeUpdatedAwardGroupForStaffAt(index: number): void {
    this.updatedAwardGroupsForStaff.splice(index, 1);
  }

  @action
  addAwardLeadToDelete(awardLead: AwardLead): void {
    this.awardLeadsToDelete.push(awardLead);
  }

  @action
  removeAwardLeadToDelete(awardLeadId: number): void {
    this.awardLeadsToDelete = this.awardLeadsToDelete.filter((awardLead) => awardLead.awardLeadId !== awardLeadId);
  }

  @action
  addGroupLeadToDelete(groupLead: GroupLead): void {
    this.groupLeadsToDelete.push(groupLead);
  }

  @action
  removeGroupLeadToDelete(groupLeadId: number): void {
    this.groupLeadsToDelete = this.groupLeadsToDelete.filter((groupLead) => groupLead.groupLeadId !== groupLeadId);
  }

  @action
  addAwardTemplateToDelete(awardTemplate: AwardExecutiveSummaryFileTemplate): void {
    this.awardTemplatesToDelete.push(awardTemplate);
  }

  @action
  removeAwardTemplateToDelete(awardId: number): void {
    this.awardTemplatesToDelete = this.awardTemplatesToDelete.filter((awardTemplate) => awardTemplate.awardId !== awardId);
  }

  @action
  removeJudgingQuestionToDelete(awardId: number, questionId: number): void {
    this.judgingQuestionsToDelete = this.judgingQuestionsToDelete.filter((judgingQuestion) =>
      judgingQuestion.awardId !== awardId || judgingQuestion.questionId !== questionId);
  }

  @action
  setEditedAwardField<K extends keyof Award>(updatedField: { key: K; value: Award[K] }): void {
    if (!this.editedAwardForStaff) return;

    const { key, value } = updatedField;
    this.editedAwardForStaff = {
      ...this.editedAwardForStaff,
      award: {
        ...this.editedAwardForStaff.award,
        [key]: value
      }
    };
  }

  @action
  setSpecializationDesignations(specializationDesignations: DropdownOption<string>[]) {
    this.specializationOptions = specializationDesignations;
  }

  @action
  setDesignationOptions(designationOptions: DropdownOption<string>[]) {
    this.designationOptions = designationOptions;
  }

  @action
  handleOnDesignationsChange(updatedDesignations: string[]) {
    if (!this.editedAwardForStaff) return;
    const designationCodes = this.designationOptions.map((option) => option.value);
    const nonDesignationCodes = this.editedAwardForStaff.award.eligibilityRuleCodes.filter((code) => !designationCodes.includes(code));
    const updatedCodes = [...nonDesignationCodes, ...updatedDesignations];

    this.editedAwardForStaff.award.eligibilityRuleCodes.splice(0, this.editedAwardForStaff.award.eligibilityRuleCodes.length, ...updatedCodes)
  }

  @action
  handleOnSpecializationDesignationsChange(updatedDesignations: string[]) {
    if (!this.editedAwardForStaff) return;
    const nonSpecializationCodes = this.editedAwardForStaff.award.eligibilityRuleCodes.filter((designation) => !designation.endsWith('ASP'));
    const updatedCodes = [...nonSpecializationCodes, ...updatedDesignations];

    this.editedAwardForStaff.award.eligibilityRuleCodes.splice(0, this.editedAwardForStaff.award.eligibilityRuleCodes.length, ...updatedCodes)
  }

  @action
  addJudgingQuestion() {
    if (!this.editedAwardForStaff?.award.awardId) return;

    const ordinal = (this.editedAwardForStaff?.judgingQuestions.length ?? 0) + 1;

    const newJudgingQuestion: JudgingQuestion = {
      questionId: this.nextTemporaryId(),
      awardId: this.editedAwardForStaff?.award.awardId,
      question: '',
      ordinal,
    }

    this.editedAwardForStaff?.judgingQuestions.push(newJudgingQuestion);
  }

  @action
  deleteJudgingQuestion(judgingQuestion: JudgingQuestion) {
    const judgingQuestionIndex = this.editedAwardForStaff?.judgingQuestions.indexOf(judgingQuestion);
    if (judgingQuestionIndex === undefined) return;

    this.editedAwardForStaff?.judgingQuestions.splice(judgingQuestionIndex, 1);

    if (judgingQuestion.questionId > 0)
      this.judgingQuestionsToDelete.push(judgingQuestion);
  }

  @action
  setJudgeCapacityOptions(options: DropdownOption<number>[]) {
    this.judgeCapacityOptions = options;
  }

  @computed
  get selectedJudgeCapacityOptions(): string[] {
    if (!this.editedAwardForStaff) return [];
    return [String(this.editedAwardForStaff.award.judgeCapacity)];
  }

  @computed
  get selectedJudgeCapacityValue(): string {
    if (!this.editedAwardForStaff) return '';
    return this.judgeCapacityOptions.find((option) => option.value === this.editedAwardForStaff?.award.judgeCapacity)?.text ?? '';
  }

  @computed
  get isJudgingQuestionTotalWeightValid(): boolean {
    if (!this.editedAwardForStaff) return false;

    const totalJudgingQuestionWeight = sumBy(this.editedAwardForStaff.judgingQuestions, (item) => item.weight ?? 0)
    return (totalJudgingQuestionWeight <= 0 || totalJudgingQuestionWeight > 100);
  }

  @computed
  get judgingQuestionValidations(): ValidationState<JudgingQuestionValidation>[] {
    return this.editedAwardForStaff?.judgingQuestions.map(judgingQuestion =>
      createValidationState(judgingQuestionValidationSchema.safeParse(judgingQuestion))) ?? [];
  }

  @computed
  get totalJudgingQuestionWeight(): { isValid: boolean, value: number } {
    if (!this.editedAwardForStaff) return { isValid: false, value: 0 };
    const questions = this.editedAwardForStaff.judgingQuestions;
    const numberOfQuestions = questions.length;

    if (numberOfQuestions === 0) {
      return { isValid: true, value: 0 };
    }

    const totalWeight = sumBy(questions, (question) => question.weight ?? 0);
    const evenlyDistributedTotal = Math.floor(100 / numberOfQuestions) * numberOfQuestions;

    return {
      isValid: totalWeight === 100 || totalWeight === evenlyDistributedTotal,
      value: totalWeight
    };
  }

  @computed
  get isJudgingQuestionValid(): boolean {
    return this.judgingQuestionValidations.every(validation => validation.isValid)
      && this.totalJudgingQuestionWeight.isValid;
  }

  /* Local helper functions */

  private nextTempId = -1;

  private nextTemporaryId(): number {
    return this.nextTempId--;
  }
}
